import { HttpResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import { NotificationsService } from './notifications.service';

/** Methods that can produce new activity worth polling for. */
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Pokes {@link NotificationsService.notifyMutation} after any successful user
 * write, so the notifications view can pull the delta cursor immediately
 * rather than waiting up to 5s for its poll tick.
 *
 * Excludes the notification endpoints themselves (mark-read / mark-all-read /
 * dismiss) — those are reactions to notifications, not activity that creates
 * them, and self-triggering would loop — and `/auth` (login/refresh/logout are
 * not user activity).
 */
export const notificationRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const isWrite = WRITE_METHODS.has(req.method.toUpperCase());
  const isSelf = req.url.includes('/notifications') || req.url.includes('/auth');
  if (!isWrite || isSelf) {
    return next(req);
  }

  const notifications = inject(NotificationsService);
  return next(req).pipe(
    tap((event) => {
      // Only the final response counts — upload-progress events also flow
      // through here and must not each trigger a poll.
      if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
        notifications.notifyMutation();
      }
    }),
  );
};
